/**
 * Plays decoded audio on the AudioContext timeline, so it is sample-locked to
 * a clock driven by the same context (audioContextTimeSource). Media elements
 * cannot give this: WebKit restarts element output tens of ms after `seeked`,
 * and reports currentTime ahead of what is heard (spike 0.1a).
 *
 * @module
 */
import { monoMix, Wsola } from "../audio/stretch";
import type { MediaLike } from "./controller";

/** How far ahead of the context's current time a start is scheduled, so it is never late. */
const SCHEDULE_LEAD_S = 0.02;
/** Stretched audio is scheduled in blocks this long, kept this far ahead. */
const BLOCK_S = 0.25;
const AHEAD_S = 0.6;
const PUMP_MS = 50;
/** Segments closer than this count as continuous. */
const JOIN_S = 0.001;

/** Speeds with sound: 1×, and 0.5–2× time-stretched to keep the pitch. */
export function audibleAt(rate: number): boolean {
  return rate >= 0.5 && rate <= 2;
}

/** Decoded audio for track time `startS` on; the first `skipS` of `buffer` is not played. */
export interface AudioSegment {
  startS: number;
  skipS: number;
  buffer: AudioBuffer;
}

const segmentEnd = (s: AudioSegment) => s.startS + s.buffer.duration - s.skipS;

export class ScheduledAudio implements MediaLike {
  readonly readyState = 4;
  readonly seeking = false;
  /** Track length; Infinity until known. */
  duration = Infinity;
  private segments: AudioSegment[] = [];
  private readonly samples = new Map<AudioSegment, { channels: Float32Array[]; mono: Float32Array }>();
  private playing = false;
  private nodes: AudioBufferSourceNode[] = [];
  /** Track time and context time up to which output is scheduled. */
  private sched: { pos: number; at: number; wsola: { seg: AudioSegment; w: Wsola } | null } | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  /** Track position at context time `anchorCtx`. */
  private anchorPos = 0;
  private anchorCtx = 0;
  private rate = 1;

  constructor(
    private readonly ctx: BaseAudioContext,
    private readonly output: AudioNode,
  ) {}

  /** A whole decoded file, `primingS` of encoder delay at its start (AAC: 1024 samples). */
  static of(ctx: BaseAudioContext, buffer: AudioBuffer, output: AudioNode, primingS = 0): ScheduledAudio {
    const a = new ScheduledAudio(ctx, output);
    a.add({ startS: 0, skipS: primingS, buffer });
    a.duration = buffer.duration - primingS;
    return a;
  }

  add(segment: AudioSegment): void {
    this.segments = [...this.segments, segment].sort((x, y) => x.startS - y.startS);
  }

  /** Frees segments that end before `t`. */
  dropBefore(t: number): void {
    const gone = this.segments.filter((s) => segmentEnd(s) < t);
    if (gone.length === 0) return;
    for (const s of gone) this.samples.delete(s);
    this.segments = this.segments.filter((s) => !gone.includes(s));
  }

  get buffered() {
    const ranges: [number, number][] = [];
    for (const s of this.segments) {
      const last = ranges[ranges.length - 1];
      if (last && s.startS <= last[1] + JOIN_S) last[1] = Math.max(last[1], segmentEnd(s));
      else ranges.push([s.startS, segmentEnd(s)]);
    }
    return {
      length: ranges.length,
      start: (i: number) => ranges[i]?.[0] ?? 0,
      end: (i: number) => ranges[i]?.[1] ?? 0,
    };
  }

  get paused(): boolean {
    return !this.playing;
  }

  get currentTime(): number {
    if (!this.playing) return this.anchorPos;
    return this.anchorPos + Math.max(0, this.ctx.currentTime - this.anchorCtx) * this.rate;
  }

  set currentTime(pos: number) {
    this.anchorPos = Math.max(0, pos);
    if (this.playing) this.start();
  }

  get playbackRate(): number {
    return this.rate;
  }

  set playbackRate(rate: number) {
    if (rate === this.rate) return;
    this.anchorPos = this.currentTime;
    this.anchorCtx = this.ctx.currentTime;
    this.rate = rate;
    if (this.playing) this.start();
  }

  play(): Promise<void> {
    if (!this.playing) this.start();
    return Promise.resolve();
  }

  pause(): void {
    if (!this.playing) return;
    this.anchorPos = this.currentTime;
    this.playing = false;
    this.stop();
  }

  /** Starts at the next scheduling slot, at the position the clock will have reached by then. */
  private start(): void {
    const pos = this.playing ? this.currentTime : this.anchorPos;
    this.stop();
    this.playing = true;
    const when = this.ctx.currentTime + SCHEDULE_LEAD_S;
    const startPos = pos + SCHEDULE_LEAD_S * this.rate;
    this.anchorPos = startPos;
    this.anchorCtx = when;
    if (!audibleAt(this.rate)) return;
    this.sched = { pos: startPos, at: when, wsola: null };
    this.pump();
    this.timer = setInterval(() => this.pump(), PUMP_MS);
  }

  /**
   * Schedules output back to back until AHEAD_S ahead: at 1× each segment's
   * remainder as one node, otherwise stretched blocks. Audio not decoded in
   * time is skipped, so what plays stays where the clock is.
   */
  private pump(): void {
    const s = this.sched;
    if (!s) return;
    const horizon = this.ctx.currentTime + AHEAD_S;
    while (s.at < horizon && s.pos < this.duration) {
      const late = this.ctx.currentTime + SCHEDULE_LEAD_S - s.at;
      if (late > 0) {
        s.at += late;
        s.pos += late * this.rate;
        s.wsola = null;
      }
      const seg = this.segments.find((x) => x.startS <= s.pos + JOIN_S && s.pos < segmentEnd(x) - JOIN_S);
      if (!seg) return;
      const sr = seg.buffer.sampleRate;
      if (this.rate === 1) {
        const offset = seg.skipS + Math.max(0, s.pos - seg.startS);
        const length = segmentEnd(seg) - Math.max(s.pos, seg.startS);
        this.schedule(seg.buffer, s.at, offset);
        s.at += length;
        s.pos += length;
        continue;
      }
      if (s.wsola?.seg !== seg) {
        const samples = this.samplesOf(seg);
        const from = Math.round((seg.skipS + s.pos - seg.startS) * sr);
        s.wsola = { seg, w: new Wsola(samples.channels, samples.mono, this.rate, from) };
      }
      const left = Math.round(((segmentEnd(seg) - s.pos) / this.rate) * sr);
      const data = s.wsola.w.render(Math.max(1, Math.min(Math.round(BLOCK_S * sr), left)));
      const length = data[0]?.length ?? 0;
      if (length === 0) return;
      const block = this.ctx.createBuffer(data.length, length, sr);
      data.forEach((d, c) => block.copyToChannel(d, c));
      this.schedule(block, s.at, 0);
      s.at += length / sr;
      s.pos += (length / sr) * this.rate;
    }
  }

  private schedule(buffer: AudioBuffer, at: number, offset: number): void {
    const node = this.ctx.createBufferSource();
    node.buffer = buffer;
    node.connect(this.output);
    node.onended = () => {
      this.nodes = this.nodes.filter((n) => n !== node);
      node.disconnect();
    };
    node.start(at, offset);
    this.nodes.push(node);
  }

  private samplesOf(seg: AudioSegment): { channels: Float32Array[]; mono: Float32Array } {
    let s = this.samples.get(seg);
    if (!s) {
      const channels = Array.from({ length: seg.buffer.numberOfChannels }, (_, c) => seg.buffer.getChannelData(c));
      s = { channels, mono: monoMix(channels) };
      this.samples.set(seg, s);
    }
    return s;
  }

  private stop(): void {
    if (this.timer !== null) clearInterval(this.timer);
    this.timer = null;
    this.sched = null;
    for (const n of this.nodes) {
      n.onended = null;
      n.stop();
      n.disconnect();
    }
    this.nodes = [];
  }
}
